/**
 * Zenbivy Bundle Builder
 * Self-contained vanilla ES6+ bundle builder for Shopify.
 * Renders step-by-step option selectors and hooks into the native
 * product-form Add to Cart button to add all selected variants at once.
 */

(function () {
  'use strict';

  class ZenvivyBundleBuilder {
    constructor(el) {
      this.el = el;
      this.stepsEl = el.querySelector('#zbb-steps');
      this.footerEl = el.querySelector('#zbb-footer');
      this.summaryEl = el.querySelector('#zbb-summary');

      // Read configuration from data attributes
      this.handles = [
        el.dataset.product1,
        el.dataset.product2,
        el.dataset.product3,
      ].filter(Boolean);

      this.labels = [
        el.dataset.label1 || 'Step 1',
        el.dataset.label2 || 'Step 2',
        el.dataset.label3 || 'Step 3',
      ];

      // products[i] = full product JSON
      this.products = [];
      // selectedOptions[i] = { optionIndex: value, ... }
      this.selectedOptions = [];
      // selectedVariants[i] = variant object or null
      this.selectedVariants = [];

      // Guard against double-submission
      this._isAdding = false;

      this.init();
    }

    // ─── Init ────────────────────────────────────────────────────────────────

    async init() {
      try {
        await this.fetchAllProducts(this.handles);
        this.renderSteps();
        this.hookNativeButton();
      } catch (err) {
        this.stepsEl.innerHTML =
          '<div class="zbb-loading">Failed to load options. Please refresh the page.</div>';
        console.error('[ZenvivyBundleBuilder] init error:', err);
      }
    }

    // ─── Fetch ───────────────────────────────────────────────────────────────

    async fetchAllProducts(handles) {
      // Prefer Liquid-pre-rendered data embedded in the page. This works in
      // the theme editor preview, on password-protected stores, and avoids
      // extra network round-trips on the live store.
      const dataEl = document.getElementById('zbb-products-data');
      if (dataEl) {
        const parsed = JSON.parse(dataEl.textContent);
        this.products = parsed.filter(Boolean);
        return;
      }

      // Fallback: fetch from the Shopify storefront API (live store, no password).
      if (!handles.length) return;
      const fetches = handles.map((handle) =>
        fetch(`/products/${handle}.js`).then((r) => {
          if (!r.ok) throw new Error(`Failed to fetch product: ${handle}`);
          return r.json();
        })
      );
      this.products = await Promise.all(fetches);
    }

    // ─── Render ──────────────────────────────────────────────────────────────

    renderSteps() {
      this.stepsEl.innerHTML = '';

      this.products.forEach((product, index) => {
        // Initialise selection state for this product
        this.selectedOptions[index] = {};
        this.selectedVariants[index] = null;

        const stepEl = this.buildStepElement(product, index);
        this.stepsEl.appendChild(stepEl);

        // Auto-select first available variant options
        this.autoSelectFirstAvailable(product, index);
      });

      this.refreshAllSoldOutStates();
      this.updateSummary();
    }

    buildStepElement(product, index) {
      const step = document.createElement('div');
      step.className = 'zbb-step';
      step.dataset.stepIndex = index;

      // Step header
      const priceRange = this.getPriceRange(product);
      const head = document.createElement('div');
      head.className = 'zbb-step-head';
      head.innerHTML = `
        <span class="zbb-step-num">${index + 1}</span>
        <span class="zbb-step-name">${this.escapeHtml(this.labels[index])}</span>
        <span class="zbb-step-price" data-step-price="${index}">${priceRange}</span>
      `;
      step.appendChild(head);

      // Step body — wraps option groups, chips, and OOS message for padding
      const body = document.createElement('div');
      body.className = 'zbb-step-body';

      // Option groups
      product.options.forEach((optionName, optIdx) => {
        const isFirstOption = optIdx === 0;
        const values = this.getOptionValues(product, optIdx);
        const useCards = isFirstOption && this.shouldUseCardPills(optionName, values, product);

        const group = document.createElement('div');
        group.className = 'zbb-option-group';
        group.dataset.optionIndex = optIdx;

        const label = document.createElement('div');
        label.className = 'zbb-option-label';
        label.textContent = optionName;
        group.appendChild(label);

        if (useCards) {
          group.appendChild(this.buildCardPills(product, optIdx, values, index));
        } else {
          group.appendChild(this.buildTextPills(product, optIdx, values, index));
        }

        body.appendChild(group);
      });

      // Out-of-stock message placeholder
      const oosMsg = document.createElement('div');
      oosMsg.className = 'zbb-oos-msg';
      oosMsg.style.display = 'none';
      oosMsg.dataset.oosStep = index;
      body.appendChild(oosMsg);

      // Selection summary chips — populated by updateSelectionChips()
      const chips = document.createElement('div');
      chips.className = 'zbb-selection-chips';
      body.appendChild(chips);

      step.appendChild(body);
      return step;
    }

    buildTextPills(product, optIdx, values, stepIndex) {
      const container = document.createElement('div');
      container.className = 'zbb-pills';

      values.forEach((value) => {
        const pill = document.createElement('button');
        pill.className = 'zbb-pill';
        pill.type = 'button';
        pill.textContent = value;
        pill.dataset.optionIndex = optIdx;
        pill.dataset.value = value;

        pill.addEventListener('click', () => {
          this.onOptionSelect(stepIndex, optIdx, value, pill);
        });

        container.appendChild(pill);
      });

      return container;
    }

    buildCardPills(product, optIdx, values, stepIndex) {
      const container = document.createElement('div');
      container.className = 'zbb-card-pills';

      const featuredImageSrc = product.featured_image
        ? this.cleanImageUrl(product.featured_image)
        : null;

      values.forEach((value) => {
        // Try to find a product image whose alt or src matches this value
        const matchedImage = this.findImageForValue(product, value);
        const imgSrc = matchedImage || featuredImageSrc;

        const pill = document.createElement('div');
        pill.className = 'zbb-card-pill';
        pill.dataset.optionIndex = optIdx;
        pill.dataset.value = value;
        pill.setAttribute('role', 'button');
        pill.setAttribute('tabindex', '0');

        // Split parenthetical sub-labels (e.g. "Name (COMING SOON)")
        const parenMatch = value.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
        const mainLabel = parenMatch ? parenMatch[1].trim() : value;
        const subLabel  = parenMatch ? parenMatch[2].trim() : '';
        pill.innerHTML = `
          ${imgSrc ? `<img src="${this.escapeHtml(imgSrc)}" alt="${this.escapeHtml(mainLabel)}" loading="lazy">` : ''}
          <div class="zbb-cp-name">${this.escapeHtml(mainLabel)}${subLabel ? `<span class="zbb-cp-sub">${this.escapeHtml(subLabel)}</span>` : ''}</div>
        `;

        const handleClick = () => {
          this.onOptionSelect(stepIndex, optIdx, value, pill);
        };

        pill.addEventListener('click', handleClick);
        pill.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        });

        container.appendChild(pill);
      });

      return container;
    }

    // ─── Option Selection ────────────────────────────────────────────────────

    onOptionSelect(stepIndex, optionIndex, value, pillEl) {
      const product = this.products[stepIndex];

      // Update selection state
      this.selectedOptions[stepIndex][optionIndex] = value;

      // Update pill UI for this option group
      const step = this.stepsEl.querySelector(`[data-step-index="${stepIndex}"]`);
      const group = step.querySelector(`[data-option-index="${optionIndex}"]`);
      group.querySelectorAll('.zbb-pill, .zbb-card-pill').forEach((p) => {
        p.classList.remove('selected');
      });
      pillEl.classList.add('selected');

      // Find matching variant
      const variant = this.findMatchingVariant(product, this.selectedOptions[stepIndex]);
      this.selectedVariants[stepIndex] = variant;

      // Update step price
      this.updateStepPrice(stepIndex, variant);

      // Update OOS message
      this.updateOosMessage(stepIndex, variant);

      // Refresh sold-out states for all steps (because a selection change
      // may affect availability of sibling options)
      this.refreshSoldOutStates(stepIndex);

      this.updateSummary();
      this.updateSelectionChips(stepIndex);
    }

    findMatchingVariant(product, selectedOptions) {
      return (
        product.variants.find((v) => {
          return product.options.every((_, i) => {
            const key = `option${i + 1}`;
            return selectedOptions[i] === undefined || v[key] === selectedOptions[i];
          });
        }) || null
      );
    }

    // ─── Auto-select first available ─────────────────────────────────────────

    autoSelectFirstAvailable(product, stepIndex) {
      // Find the first available variant
      const firstAvailable =
        product.variants.find((v) => v.available) || product.variants[0];

      if (!firstAvailable) return;

      product.options.forEach((_, optIdx) => {
        const key = `option${optIdx + 1}`;
        const value = firstAvailable[key];
        if (value == null) return;

        this.selectedOptions[stepIndex][optIdx] = value;

        // Mark the corresponding pill as selected
        const step = this.stepsEl.querySelector(`[data-step-index="${stepIndex}"]`);
        if (!step) return;
        const group = step.querySelector(`[data-option-index="${optIdx}"]`);
        if (!group) return;

        group.querySelectorAll('.zbb-pill, .zbb-card-pill').forEach((p) => {
          if (p.dataset.value === value) {
            p.classList.add('selected');
          }
        });
      });

      const variant = this.findMatchingVariant(product, this.selectedOptions[stepIndex]);
      this.selectedVariants[stepIndex] = variant;
      this.updateStepPrice(stepIndex, variant);
      this.updateOosMessage(stepIndex, variant);
      this.updateSelectionChips(stepIndex);
    }

    // ─── Sold-out state ──────────────────────────────────────────────────────

    refreshAllSoldOutStates() {
      this.products.forEach((_, i) => this.refreshSoldOutStates(i));
    }

    refreshSoldOutStates(stepIndex) {
      const product = this.products[stepIndex];
      const step = this.stepsEl.querySelector(`[data-step-index="${stepIndex}"]`);
      if (!step) return;

      product.options.forEach((_, optIdx) => {
        const group = step.querySelector(`[data-option-index="${optIdx}"]`);
        if (!group) return;

        group.querySelectorAll('.zbb-pill, .zbb-card-pill').forEach((pill) => {
          const value = pill.dataset.value;

          // Build a test selection: current selections + this candidate value
          const testOptions = Object.assign({}, this.selectedOptions[stepIndex], {
            [optIdx]: value,
          });

          // Check if any available variant matches this combination
          const hasAvailable = product.variants.some((v) => {
            const matches = product.options.every((_, i) => {
              const key = `option${i + 1}`;
              return testOptions[i] === undefined || v[key] === testOptions[i];
            });
            return matches && v.available;
          });

          if (hasAvailable) {
            pill.classList.remove('sold-out');
          } else {
            pill.classList.add('sold-out');
          }
        });
      });
    }

    // ─── Price helpers ───────────────────────────────────────────────────────

    getPriceRange(product) {
      const prices = product.variants.map((v) => v.price);
      const min = Math.min(...prices);
      return `from ${this.formatPrice(min)}`;
    }

    updateStepPrice(stepIndex, variant) {
      const priceEl = this.stepsEl.querySelector(`[data-step-price="${stepIndex}"]`);
      if (!priceEl) return;
      priceEl.textContent = variant
        ? this.formatPrice(variant.price)
        : this.getPriceRange(this.products[stepIndex]);
    }

    formatPrice(cents) {
      return (cents / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      });
    }

    // ─── OOS message ─────────────────────────────────────────────────────────

    updateOosMessage(stepIndex, variant) {
      const msgEl = this.stepsEl.querySelector(`[data-oos-step="${stepIndex}"]`);
      if (!msgEl) return;
      if (variant && !variant.available) {
        msgEl.textContent = 'Currently out of stock';
        msgEl.style.display = 'block';
      } else {
        msgEl.style.display = 'none';
      }
    }

    // ─── Summary + Bundle Button ──────────────────────────────────────────────

    updateSummary() {
      let html = '';
      let total = 0;
      let anySelected = false;

      const allSelected = this.selectedVariants.length === this.products.length &&
        this.selectedVariants.every((v) => v !== null);

      this.products.forEach((product, i) => {
        const variant = this.selectedVariants[i];
        if (!variant) return;

        anySelected = true;

        const variantTitle =
          variant.title && variant.title !== 'Default Title' ? variant.title : '';
        const displayTitle = variantTitle
          ? `${product.title} — ${variantTitle}`
          : product.title;

        total += variant.price;

        html += `
          <div class="zbb-summary-line">
            <span class="label">${this.escapeHtml(displayTitle)}</span>
            <span class="price">${this.formatPrice(variant.price)}</span>
          </div>
        `;
      });

      if (allSelected) {
        html += `<hr class="zbb-summary-divider">`;
        html += `
          <div class="zbb-summary-line total">
            <span class="label">Bundle Total</span>
            <span class="price">${this.formatPrice(total)}</span>
          </div>
        `;
      }

      this.summaryEl.innerHTML = html;

      if (anySelected) {
        this.footerEl.style.display = 'block';
      }
    }

    // ─── Selection summary chips ──────────────────────────────────────────────
    // Renders small chips below each step showing the currently chosen options.

    updateSelectionChips(stepIndex) {
      const step = this.stepsEl.querySelector(`[data-step-index="${stepIndex}"]`);
      if (!step) return;
      const container = step.querySelector('.zbb-selection-chips');
      if (!container) return;

      const product = this.products[stepIndex];
      const selections = this.selectedOptions[stepIndex];

      container.innerHTML = '';
      product.options.forEach((_, optIdx) => {
        const val = selections[optIdx];
        if (!val) return;
        const chip = document.createElement('span');
        chip.className = 'zbb-chip';
        chip.textContent = val;
        container.appendChild(chip);
      });
    }

    // ─── Native button hook ───────────────────────────────────────────────────
    // Intercepts the native "Add to Cart" click so the bed builder controls the
    // entire cart-add flow. The "zenbivy light bed" product itself is not a
    // standalone purchasable item — the quilt and sheet ARE the purchase.

    hookNativeButton() {
      const productFormEl = document.querySelector('product-form');
      console.log('[ZBB] hookNativeButton: product-form found?', !!productFormEl);
      if (!productFormEl) return;

      const submitBtn = productFormEl.querySelector('[type="submit"]');
      console.log('[ZBB] hookNativeButton: submit button found?', !!submitBtn);
      if (!submitBtn) return;

      this._nativeBtn = submitBtn;
      this._productFormEl = productFormEl;

      // Intercept both click (mouse/touch) and submit (keyboard/programmatic)
      submitBtn.addEventListener('click', (evt) => {
        console.log('[ZBB] submit button CLICK intercepted');
        evt.preventDefault();
        evt.stopImmediatePropagation();
        this.handleBundleAdd();
      }, { capture: true });

      const formEl = productFormEl.querySelector('form');
      if (formEl) {
        formEl.addEventListener('submit', (evt) => {
          console.log('[ZBB] form SUBMIT intercepted');
          evt.preventDefault();
          evt.stopImmediatePropagation();
          this.handleBundleAdd();
        }, { capture: true });
      }
    }

    async handleBundleAdd() {
      console.log('[ZBB] handleBundleAdd called, _isAdding:', this._isAdding);
      if (this._isAdding) return;
      this._isAdding = true;

      const btn = this._nativeBtn;
      const spinner = this._productFormEl
        ? this._productFormEl.querySelector('.loading-overlay__spinner')
        : null;

      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('loading');
      if (spinner) spinner.classList.remove('hidden');

      try {
        const items = this.selectedVariants
          .filter((v) => v !== null && v !== undefined)
          .map((v) => ({ id: v.id, quantity: 1 }));

        console.log('[ZBB] selectedVariants:', this.selectedVariants);
        console.log('[ZBB] items to add:', items);

        if (!items.length) {
          console.log('[ZBB] no items selected, aborting');
          return;
        }

        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });
        console.log('[ZBB] /cart/add.js status:', addResponse.status);

        if (!addResponse.ok) {
          const errData = await addResponse.json().catch(() => ({}));
          console.error('[ZBB] cart add error:', errData);
          throw new Error(errData.description || 'Could not add items to cart.');
        }

        const addData = await addResponse.json();
        console.log('[ZBB] /cart/add.js response:', addData);

        // Fetch updated section HTML and render the cart drawer
        const cartEl =
          document.querySelector('cart-drawer') ||
          document.querySelector('cart-notification');

        console.log('[ZBB] cartEl found?', !!cartEl, cartEl?.tagName);

        if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
          const sectionIds = cartEl.getSectionsToRender().map((s) => s.id);
          console.log('[ZBB] fetching sections:', sectionIds);
          const sectionsResp = await fetch(
            window.location.pathname + '?sections=' + sectionIds.join(',')
          );
          console.log('[ZBB] sections fetch status:', sectionsResp.status);
          if (sectionsResp.ok) {
            const sectionsData = await sectionsResp.json();
            console.log('[ZBB] sections keys:', Object.keys(sectionsData));
            // Remove is-empty BEFORE renderContents — product-form.js normally does
            // this in its finally block, but we bypass onSubmitHandler entirely.
            // Without it, CSS hides the items list (cart-drawer.is-empty changes
            // .drawer__inner to display:grid which collapses cart-drawer-items flex:1).
            cartEl.classList.remove('is-empty');
            cartEl.renderContents({ sections: sectionsData });
            return;
          }
        }

        // Fallback: open drawer without re-rendering
        if (cartEl && typeof cartEl.open === 'function') cartEl.open();

      } catch (err) {
        console.error('[ZBB] handleBundleAdd error:', err);
      } finally {
        this._isAdding = false;
        btn.removeAttribute('aria-disabled');
        btn.classList.remove('loading');
        if (spinner) spinner.classList.add('hidden');
      }
    }

    // ─── Utility ─────────────────────────────────────────────────────────────

    getOptionValues(product, optionIndex) {
      const seen = new Set();
      const values = [];
      product.variants.forEach((v) => {
        const val = v[`option${optionIndex + 1}`];
        if (val != null && !seen.has(val)) {
          seen.add(val);
          values.push(val);
        }
      });
      return values;
    }

    shouldUseCardPills(optionName, values, product) {
      const cardOptionNames = ['style', 'type', 'footbox', 'design', 'model'];
      const lowerName = optionName.toLowerCase();
      if (cardOptionNames.some((n) => lowerName.includes(n))) {
        return true;
      }
      if (values.length >= 2 && values.length <= 4 && product.images && product.images.length > 0) {
        return true;
      }
      return false;
    }

    findImageForValue(product, value) {
      if (!product.images || product.images.length === 0) return null;
      const lowerVal = value.toLowerCase();

      const altMatch = product.images.find((img) => {
        const alt = (img.alt || '').toLowerCase();
        return alt.includes(lowerVal);
      });
      if (altMatch) {
        return this.cleanImageUrl(altMatch.src || altMatch);
      }

      const srcMatch = product.images.find((img) => {
        const src = (img.src || img || '').toLowerCase();
        return src.includes(lowerVal.replace(/\s+/g, '-'));
      });
      if (srcMatch) {
        return this.cleanImageUrl(srcMatch.src || srcMatch);
      }

      return null;
    }

    cleanImageUrl(src) {
      if (!src) return null;
      return src.replace(/(_\d+x\d*|_x\d+)(\.[a-z]+)(\?.*)?$/i, '$2$3')
                .replace(/\?.*$/, '') + '?width=128';
    }

    escapeHtml(str) {
      if (!str) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  // ─── Bootstrap ─────────────────────────────────────────────────────────────

  function init() {
    const el = document.getElementById('zenbivy-bundle-builder');
    if (!el) return;

    if (el.dataset.zbbReady) return;
    el.dataset.zbbReady = 'true';

    new ZenvivyBundleBuilder(el);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function () {
    const el = document.getElementById('zenbivy-bundle-builder');
    if (!el) return;
    delete el.dataset.zbbReady;
    init();
  });

  document.addEventListener('shopify:block:select', function () {
    const el = document.getElementById('zenbivy-bundle-builder');
    if (!el) return;
    if (el.dataset.zbbReady) return;
    init();
  });
})();
